import type { AxiosInstance } from 'axios';
import { unwrap, type ObjectResponse } from '../core/envelope';

/**
 * Body for `POST /api/Migration/Run`. When `tenantId` is omitted the
 * backend generates a fresh GUID; when supplied, it must be a valid
 * (and unregistered) GUID. The remaining fields build the connection
 * string the new tenant catalog will use.
 */
export interface MigrationRunRequest {
  tenantId?: string | null;
  dataSource: string;
  initialCatalog: string;
  userId: string;
  password: string;
  integratedSecurity: boolean;
  minPoolSize: number;
  maxPoolSize: number;
  connectTimeout: number;
  trustServerCertificate: boolean;
  encrypt: boolean;
  applicationName: string;
}

/**
 * Response from a successful `POST /api/Migration/Run`. The seeder
 * populates menus + roles + permissions + bootstrap admin and reports
 * the resulting counts so callers can sanity-check the run.
 */
export interface MigrationRunResponse {
  tenantId: string;
  adminUserId: string;
  menuCount: number;
  permissionCount: number;
  roleCount: number;
  message?: string | null;
}

export interface MigrationService {
  run(body: MigrationRunRequest): Promise<MigrationRunResponse>;
}

export function createMigrationService(client: AxiosInstance): MigrationService {
  return {
    async run(body) {
      const res = await client.post<ObjectResponse<MigrationRunResponse>>(
        '/Migration/Run',
        body
      );
      return unwrap(res.data);
    },
  };
}

/**
 * Defaults matching the connection-parameter shape contract M1
 * specifies. The form uses these as initial values; only the
 * tenant-specific fields (tenantId / dataSource / initialCatalog /
 * userId / password / applicationName) realistically need editing.
 */
export const DEFAULT_MIGRATION_REQUEST: MigrationRunRequest = {
  tenantId: null,
  dataSource: '',
  initialCatalog: '',
  userId: '',
  password: '',
  integratedSecurity: true,
  minPoolSize: 5,
  maxPoolSize: 50,
  connectTimeout: 0,
  trustServerCertificate: true,
  encrypt: true,
  applicationName: 'KSS-Mart',
};
